import Drawer from "@via/components/drawer.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import {
  OAUTH_STORAGE_LABELS,
  type OAuthConnectionSummary,
  type OAuthStorageProvider,
  REMOTE_STORAGE_REFUSAL_MESSAGES,
} from "common";
import { type JSX, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OAuthConnectionForm } from "./oauth-connection-form.tsx";
import { remoteStorageErrorMessage } from "./remote-storage.api.tsx";
import {
  rememberOAuthReturn,
  useDeleteOAuthConnection,
  useOAuthConnections,
  useStartOAuthConnection,
} from "./remote-storage-oauth.api.tsx";

/** The Google Drive and Dropbox tiles on Connect Data Source: sign in, pick, rename or remove, then connect (data-2-1). */
export default function OAuthStorageConnector({
  provider,
}: {
  provider: OAuthStorageProvider;
}): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, isLoading } = useOAuthConnections();
  const start = useStartOAuthConnection();
  const remove = useDeleteOAuthConnection();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editing, setEditing] = useState<OAuthConnectionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const label = OAUTH_STORAGE_LABELS[provider];

  if (isLoading || !data) {
    return (
      <p className="py-6 text-center text-sm text-gray-500">
        Loading {label} connections
      </p>
    );
  }
  if (!data.providers[provider]) {
    return (
      <p className="mx-auto max-w-[850px] py-6 text-sm text-gray-700">
        {REMOTE_STORAGE_REFUSAL_MESSAGES.oauth_disabled}
      </p>
    );
  }

  const connections = data.connections.filter(c => c.provider === provider);
  const selected = connections.find(c => c.id === selectedId) ?? null;
  const datasetId = searchParams.get("datasetId");

  const signIn = async () => {
    setError(null);
    try {
      const { authorizationUrl } = await start.mutateAsync(provider);
      rememberOAuthReturn(datasetId);
      window.location.assign(authorizationUrl);
    } catch (failure) {
      setError(remoteStorageErrorMessage(failure));
    }
  };

  const connect = () => {
    if (!selected) return;
    const params = new URLSearchParams({ credentialId: String(selected.id) });
    if (datasetId) params.set("datasetId", datasetId);
    navigate(
      `${ROUTES.DATA_SOURCE_PROVIDER_REMOTE_STORAGE}?${params.toString()}`
    );
  };

  return (
    <div className="mx-auto max-w-[850px] space-y-4 pb-5 pt-6">
      <div>
        <label
          className="block text-sm font-medium leading-6 text-gray-900"
          htmlFor={`oauth-select-${provider}`}>
          {`${label} connection`}
        </label>
        <select
          id={`oauth-select-${provider}`}
          className="mt-2 block w-full rounded-md border-0 py-1.5 px-2 ring-1 ring-inset ring-gray-300 sm:text-sm"
          value={selectedId ?? ""}
          onChange={e =>
            setSelectedId(e.target.value ? Number(e.target.value) : null)
          }>
          <option value="">{`Choose a ${label} connection`}</option>
          {connections.map(c => (
            <option key={c.id} value={c.id}>
              {`${c.name} (${c.account}${c.sharedDriveName ? `, ${c.sharedDriveName}` : ""})`}
            </option>
          ))}
        </select>
        {selected && "sign_in_again" === selected.status && (
          <p className="mt-1 text-xs text-rose-600" role="alert">
            {REMOTE_STORAGE_REFUSAL_MESSAGES.sign_in_again}
          </p>
        )}
      </div>
      {error && (
        <p className="text-sm text-rose-600" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-3">
        <button
          type="button"
          disabled={start.isPending}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 disabled:opacity-50"
          onClick={() => void signIn()}>
          {`Sign in to ${label}`}
        </button>
        {selected && (
          <>
            <button
              type="button"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300"
              onClick={() => setEditing(selected)}>
              Edit
            </button>
            <button
              type="button"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-rose-600 ring-1 ring-inset ring-gray-300"
              onClick={async () => {
                if (
                  window.confirm(
                    `Remove ${selected.name}? Foundry also asks ${label} to withdraw its access.`
                  )
                ) {
                  await remove.mutateAsync(selected.id);
                  setSelectedId(null);
                }
              }}>
              Remove
            </button>
          </>
        )}
        <button
          type="button"
          disabled={!selected || "sign_in_again" === selected.status}
          onClick={connect}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
          Connect
        </button>
      </div>
      <Drawer
        id={`oauth-${provider}`}
        open={null !== editing}
        setOpen={open => !open && setEditing(null)}>
        {null !== editing ? (
          <div className="p-4">
            <h2 className="mb-4 text-lg font-semibold">{`Edit ${editing.name}`}</h2>
            <OAuthConnectionForm
              connection={editing}
              onSaved={saved => {
                setSelectedId(saved.id);
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
            />
          </div>
        ) : (
          <></>
        )}
      </Drawer>
    </div>
  );
}
